import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { emptyObject, emptyArray } from 'ui-components/default-props/empty';
import { noop } from 'ui-components/default-props/noop';

import { US } from '../../../constants/country-codes';
import { AddressFields } from '../../../address/address-fields/';
import Global from '../../../store/global-selectors';
import { BUSINESS, PERSONAL } from '../../modals/constants';
import { useYupFormValidation } from '../../../validation/use-yup-form-validation';
import { useWalletRegistrationErrorContext } from '../../modals/wallet-registration/contexts/wallet-registration-error-context';
import { WalletAddressSchema } from './wallet-address-schema';

export const WalletAddress = ({
  accountType,
  address,
  errors = emptyObject,
  onChange: onChangeProp = noop,
  onValidationChange = noop,
  validationSchema = WalletAddressSchema,
  disabled = false,
  disableEmail = false,
  type = '',
  hiddenFields = emptyArray,
  subLabels = emptyObject,
}) => {
  const homeCountry = useSelector(Global.selectSellerHomeCountry);
  // This is a temporary fix to address conflicts between the validation and personal/business address fields
  // Monetization is taking over this UI in the near future and this can be removed when that happens.
  // For a personal account, the company field is the same as the full name field and this happens right before posting
  const getAddressByAccountType = useCallback(() => {
    const isPersonal = accountType === PERSONAL;
    if (isPersonal) {
      return { ...address, company: address.name };
    }
    return address;
  }, [accountType, address]);
  const { errors: validationErrors, isValid } = useYupFormValidation({
    form: getAddressByAccountType(),
    schema: validationSchema,
  });
  useEffect(() => {
    onValidationChange?.(isValid);
  }, [onValidationChange, isValid]);

  const addressErrors = useMemo(
    () => ({
      ...validationErrors,
      ...errors,
    }),
    [validationErrors, errors],
  );
  const { showAllErrors } = useWalletRegistrationErrorContext();

  const onChange = useCallback(
    (propertyName, value) => {
      onChangeProp?.({
        propertyName,
        value,
      });
    },
    [onChangeProp],
  );

  const additionalRequiredFields = homeCountry === US ? ['phone', 'email', 'name', 'company'] : ['phone', 'email', 'name'];
  return (
    <AddressFields
      accountType={accountType}
      additionalRequiredFields={additionalRequiredFields}
      address={address}
      disabled={disabled}
      disableEmail={disableEmail}
      errors={addressErrors}
      hiddenFields={hiddenFields}
      onChange={onChange}
      showAllErrors={showAllErrors}
      subLabels={subLabels}
      type={type}
    />
  );
};

WalletAddress.propTypes = {
  accountType: PropTypes.oneOf([PERSONAL, BUSINESS]).isRequired,
  address: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  onValidationChange: PropTypes.func,
  validationSchema: PropTypes.object,
  disabled: PropTypes.bool,
  disableEmail: PropTypes.bool,
  type: PropTypes.string,
  hiddenFields: PropTypes.array,
};
